import React, { useState, useCallback, useRef, useEffect, useMemo, useContext } from 'react';
import { Form, Dropdown } from 'react-bootstrap';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  Panel,
  Background,
  BackgroundVariant,
  Handle,
  Position,
  useStore,
} from 'reactflow';
import { NodeHandlerContext } from './NodeHandlerContext';
import { nodeTypes as importedNodeTypes } from './nodeTypes';
import { useTranslation } from 'react-i18next';
import 'reactflow/dist/style.css';
import api from './../api/axios';
import './TemplateBuilder.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faCirclePlay } from '@fortawesome/free-solid-svg-icons';
import Userfront from '../auth/userfrontCompat';
import RichEmailEditor from './composer/RichEmailEditor';

import {
  faClock,
  faUsers,
  faEye,
  faCheck,
  faCommentDots,
  faCodeBranch,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';

const NODE_WIDTH = 220;
const NODE_HEIGHT = 86;
const VERTICAL_GAP = 125;
const HORIZONTAL_GAP = 280;
const END_NODE_WIDTH = 200;
const START_NODE_WIDTH = 200;

const LINKEDIN_COMPOSER_LIMITS = Object.freeze({
  invite: 300,
  message: 1500,
});

const getNodeId = () => `node_${+new Date()}_${Math.random()}`;
const getEdgeId = () => `edge_${+new Date()}_${Math.random()}`;

const createStartNode = () => ({
  id: 'start',
  type: 'customNode',
  data: {
    label: 'Start',
    icon: faCirclePlay,
    iconClass: 'text-blue-600',
    badgeBg: 'bg-blue-100',
  },
  position: { x: window.innerWidth / 2 - START_NODE_WIDTH / 2, y: 50 },
  deletable: false,
  className: 'node-start',
});

const LABEL_META = {
  Start: { icon: faCirclePlay, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  'Profile view': { icon: faEye, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  'Send invite': { icon: faUsers, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  Timer: { icon: faClock, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  Message: { icon: faCommentDots, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  Condition: { icon: faCodeBranch, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  'Send email': { icon: faEnvelope, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  'End flow': { icon: faCheck, iconClass: 'text-gray-600', badgeBg: 'bg-gray-100' },
};

const hydrateWorkflow = (workflow) => {
  if (!workflow) return workflow;
  const hydratedNodes = (workflow.nodes || []).map((n) => {
    const lbl = n?.data?.label;
    const meta = LABEL_META[lbl];
    if (!meta) return { ...n, type: n.type || 'customNode' };
    return {
      ...n,
      type: n.type || 'customNode',
      data: {
        ...n.data,
        icon: n.data?.icon ?? meta.icon,
        iconClass: n.data?.iconClass ?? meta.iconClass,
        badgeBg: n.data?.badgeBg ?? meta.badgeBg,
      },
    };
  });
  return { ...workflow, nodes: hydratedNodes };
};

const initialNodes = [createStartNode()];
const initialEdges = [];

// ---- Branch models (compact)
const BRANCH_NODE_META = {
  Condition: {
    branches: [
      { id: 'true', label: 'Yes' },
      { id: 'false', label: 'No' },
    ],
  },
  'Send invite': {
    branches: [
      { id: 'accepted', label: 'Accepted' },
      { id: 'notAccepted', label: 'Not accepted' },
    ],
  },
  Message: {
    branches: [
      { id: 'response', label: 'Response' },
      { id: 'noResponse', label: 'No response' },
    ],
  },
};

const isBranchNodeLabel = (label) => !!BRANCH_NODE_META[label];

// -------- Inline Custom Node (auto hides + when connected) ----------
const CustomNodeInline = ({ id, data }) => {
  const openAddModal = useContext(NodeHandlerContext);
  const edges = useStore((s) => s.edges);

  const label = data?.label;
  const isBranchNode = isBranchNodeLabel(label);
  const isEndNode = label === 'End flow';

  const outgoing = useMemo(() => edges.filter((e) => e.source === id), [edges, id]);
  const hasOutgoingNormal = outgoing.length > 0;

  const branches = useMemo(() => {
    if (!isBranchNode) return [];
    return BRANCH_NODE_META[label].branches;
  }, [isBranchNode, label]);

  const branchConnected = useCallback(
    (branchId) => outgoing.some((e) => (e.data?.branch === branchId) || (e.sourceHandle === branchId)),
    [outgoing]
  );

  // ✅ REQUIRED CHANGE:
  // End node should never show + icon and should never have outgoing handles.
  const showNormalAdd = !isEndNode && !isBranchNode && !hasOutgoingNormal;

  const showBranchButtons = useMemo(() => {
    if (!isBranchNode) return false;
    // show row only if at least one branch is not connected
    return branches.some((b) => !branchConnected(b.id));
  }, [isBranchNode, branches, branchConnected]);

  const conditionLabel = useMemo(() => {
    if (label !== 'Condition') return '';
    const ct = data?.conditionType || 'isConnection';
    if (ct === 'isConnection') return 'Connection exists';
    if (ct === 'hasLinkedinUrl') return 'LinkedIn URL exists';
    if (ct === 'hasEmail') return 'Email exists';
    return 'Condition';
  }, [label, data?.conditionType]);

  const subLabel = useMemo(() => {
    if (label === 'Condition') return `IF: ${conditionLabel}`;
    if (label === 'Send invite') return `When invite is…`;
    if (label === 'Message') return `When lead…`;
    if (label === 'Send email') return `When lead…`;
    if (label === 'End flow') return `Stops the workflow`;
    return '';
  }, [label, conditionLabel]);

  return (
    <div className="relative">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-3 py-2 min-w-[200px]">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${data?.badgeBg || 'bg-blue-100'}`}>
            <FontAwesomeIcon
              icon={data?.icon || faCirclePlay}
              className={`h-4 w-4 ${data?.iconClass || 'text-blue-600'}`}
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">{label}</div>
            {!!subLabel && <div className="text-xs text-gray-500 truncate">{subLabel}</div>}
          </div>

          {showNormalAdd && (
            <button
              type="button"
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 text-gray-600"
              onClick={(e) => {
                e.stopPropagation();
                if (typeof openAddModal === 'function') openAddModal(id, null);
              }}
              aria-label="Add action"
              title="Add action"
            >
              <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
            </button>
          )}
        </div>

        {isBranchNode && showBranchButtons && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            {branches.map((b) => {
              const connected = branchConnected(b.id);
              return (
                <button
                  key={b.id}
                  type="button"
                  disabled={connected}
                  className={`inline-flex items-center justify-between gap-2 px-2 py-1 rounded-md border text-[11px]
                    ${!connected ? 'border-gray-200 hover:bg-gray-50 text-gray-700' : 'border-gray-100 text-gray-300 cursor-not-allowed'}
                  `}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (connected) return;
                    if (typeof openAddModal === 'function') openAddModal(id, b.id);
                  }}
                  title={`Add action for: ${b.label}`}
                >
                  <span className="text-gray-600">{b.label}</span>
                  <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Incoming */}
      <Handle type="target" position={Position.Top} id="in" style={{ width: 10, height: 10 }} />

      {/* ✅ REQUIRED CHANGE: End flow has NO outgoing handles */}
      {!isEndNode && !isBranchNode && (
        <Handle type="source" position={Position.Bottom} id="out" style={{ width: 10, height: 10 }} />
      )}

      {!isEndNode && isBranchNode && (
        <>
          {/* Two bottom source handles. We keep positions stable for all branch nodes. */}
          <Handle
            type="source"
            position={Position.Bottom}
            id={branches?.[0]?.id || 'a'}
            style={{ width: 10, height: 10, left: '35%' }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id={branches?.[1]?.id || 'b'}
            style={{ width: 10, height: 10, left: '65%' }}
          />
        </>
      )}
    </div>
  );
};

const TemplateBuilder = ({
  id,
  currentUser,
  campaignStatus,
  setMessage,
  templateList,
  fetchTemplates,
  onFetchTemplates,
  exposeWorkflowActions,
}) => {
  const reactFlowWrapper = useRef(null);
  const richEmailEditorRef = useRef(null);
  const messageInputRef = useRef(null);
  const subjectInputRef = useRef(null);
  const [campaignAssets, setCampaignAssets] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [rfInstance, setRfInstance] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  // branch context: which branch button user clicked (for branch nodes)
  const [pendingBranch, setPendingBranch] = useState(null); // string | null

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showAddActionModal, setShowAddActionModal] = useState(false);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const { t } = useTranslation();
  const { setViewport } = useReactFlow();

  // Keep your imported nodeTypes but enforce our renderer for customNode
  const nodeTypes = useMemo(() => ({ ...(importedNodeTypes || {}), customNode: CustomNodeInline }), []);

  const refreshTemplates = useCallback(() => {
    if (typeof fetchTemplates === 'function') fetchTemplates();
  }, [fetchTemplates]);

  // --------------------
  // NEW: fetch global signature (email footer) once, so we can preview it and avoid duplicates
  // --------------------
  const [globalSignature, setGlobalSignature] = useState('');
  const [signatureLoading, setSignatureLoading] = useState(false);

  useEffect(() => {
    const fetchSignature = async () => {
      setSignatureLoading(true);
      try {
        const res = await api.get('/email/signature');
        setGlobalSignature(String(res.data?.signature || ''));
      } catch (e) {
        setGlobalSignature('');
      } finally {
        setSignatureLoading(false);
      }
    };

    fetchSignature();
  }, []);

  const refreshCampaignAssets = useCallback(async () => {
    try { const { data } = await api.get(`/campaign-assets/campaign/${id}`); setCampaignAssets(data.assets || []); } catch (_) { setCampaignAssets([]); }
  }, [id]);
  useEffect(() => { refreshCampaignAssets(); }, [refreshCampaignAssets]);

  const normalizeSig = (s) => String(s || '').trim();
  const sigBlock = (sig) => {
    const s = normalizeSig(sig);
    if (!s) return '';
    return s;
  };

  const [nodeSettings, setNodeSettings] = useState({
    // Timer-ish values for compact negative branches
    notAcceptedWaitValue: 15,
    notAcceptedWaitUnit: 'minutes', // minutes | hours | days

    noResponseWaitValue: 15,
    noResponseWaitUnit: 'minutes',

    duration: '',
    timeUnit: 'minutes',

    // message content
    message: '',
    sendMessage: false,

    // email
    emailSubject: '',
    emailBody: '',
    emailBodyHtml: '',
    attachmentAssetIds: [],

    // NEW: signature behavior per email node
    useGlobalSignature: true,
    emailSignatureOverride: '',

    // condition
    conditionType: 'isConnection',
  });

  // -------------------- positioning helpers --------------------
  const findFreePosition = useCallback(
    (startX, startY, opts = {}) => {
      const {
        maxTries = 24,
        stepY = 95,
        padX = 46,
        padY = 46,
        width = NODE_WIDTH,
        height = NODE_HEIGHT,
      } = opts;

      const rects = (nodes || []).map((n) => {
        const w = n.width ?? NODE_WIDTH;
        const h = n.height ?? NODE_HEIGHT;
        return { x: n.position?.x ?? 0, y: n.position?.y ?? 0, w, h };
      });

      const intersects = (x, y) => {
        const r1 = { x, y, w: width, h: height };
        return rects.some((r2) => {
          return !(
            r1.x + r1.w + padX < r2.x ||
            r1.x > r2.x + r2.w + padX ||
            r1.y + r1.h + padY < r2.y ||
            r1.y > r2.y + r2.h + padY
          );
        });
      };

      let x = startX;
      let y = startY;

      for (let i = 0; i < maxTries; i++) {
        if (!intersects(x, y)) return { x, y };
        y += stepY;
      }

      return { x, y };
    },
    [nodes]
  );

  // -------------------- branch edge checks --------------------
  const branchEdgeExists = useCallback(
    (nodeId, branchId) =>
      edges.some(
        (e) =>
          e.source === nodeId &&
          ((e.data?.branch === branchId) || (e.sourceHandle === branchId))
      ),
    [edges]
  );

  // -------------------- ReactFlow centering --------------------
  const { fitView, setCenter, getNodes, updateNodeInternals } = useReactFlow();
  const centerFlow = useCallback(() => {
    let tries = 0;
    const attempt = () => {
      const ns = getNodes?.() || [];
      if (!ns.length) return;

      const start = ns.find((n) => n.id === 'start' || n?.data?.label === 'Start');
      if (!start) {
        fitView({ padding: 0.2, includeHiddenNodes: false, duration: 300, minZoom: 0.55 });
        return;
      }

      updateNodeInternals?.(start.id);

      const w = start.width ?? START_NODE_WIDTH;
      const h = start.height ?? START_NODE_WIDTH;

      if ((!start.width || !start.height) && tries < 3) {
        tries += 1;
        requestAnimationFrame(attempt);
        return;
      }

      const isPortrait = window.innerHeight > window.innerWidth;
      if (isPortrait) {
        fitView({ nodes: [{ id: start.id }], padding: 0.6, duration: 180, minZoom: 0.55, maxZoom: 1 });
        setTimeout(() => {
          const cx = start.position.x + (start.width ?? w) / 2;
          const cy = start.position.y + (start.height ?? h) / 2 + 160;
          setCenter(cx, cy, { duration: 180 });
        }, 200);
      } else {
        const targetZoom = Math.min(0.95, Math.max(0.65, window.innerWidth < 900 ? 0.8 : 0.9));
        const cx = start.position.x + w / 2;
        const cy = start.position.y + h / 2 + 160;
        setCenter(cx, cy, { zoom: targetZoom, duration: 300 });
      }
    };

    requestAnimationFrame(attempt);
  }, [fitView, setCenter, getNodes, updateNodeInternals]);

  useEffect(() => {
    const onResize = () => centerFlow();
    window.addEventListener('orientationchange', onResize);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('orientationchange', onResize);
      window.removeEventListener('resize', onResize);
    };
  }, [centerFlow]);

  // -------------------- Save/restore --------------------
  const saveConfiguredWorkflow = async ({ workflow, id }) => {
    try {
      await api.post(`/campaigns/${id}/configure-workflow`, { configuredWorkflow: workflow });
      setMessage({ type: 'success', text: 'Workflow saved successfully!' });
    } catch (error) {
      console.error('Error saving configured workflow:', error.response?.data || error);
      setMessage({ type: 'error', text: 'Failed to save workflow.' });
    }
  };

  const getConfiguredWorkflow = async (id) => {
    try {
      const response = await api.get(`/campaigns/${id}`);
      return response.data.configuredWorkflow;
    } catch (error) {
      console.error('Error fetching configured workflow:', error.response?.data || error);
      return null;
    }
  };

  const onSave = useCallback(() => {
    if (!rfInstance) return;
    const flow = rfInstance.toObject();
    saveConfiguredWorkflow({ workflow: flow, id });
  }, [rfInstance, id]);

  const onRestore = useCallback(() => {
    const restoreFlow = async () => {
      const savedWorkflow = await getConfiguredWorkflow(id);
      if (!savedWorkflow) return;

      const wf = hydrateWorkflow(savedWorkflow);
      const { viewport, nodes: savedNodes, edges: savedEdges } = wf;

      if (viewport) {
        const { x = 0, y = 0, zoom = 1 } = viewport;
        setViewport({ x, y, zoom });
      }
      if (savedNodes) setNodes(savedNodes);
      if (savedEdges) setEdges(savedEdges);
    };
    restoreFlow();
  }, [id, setViewport, setNodes, setEdges]);

  useEffect(() => {
    onRestore();
  }, [onRestore]);

  // -------------------- Message rule: only when connection guaranteed --------------------
  const hasConnectionGuaranteeInPath = useCallback(
    (nodeId) => {
      let currentNodeId = nodeId;

      while (true) {
        const incomingEdge = edges.find((e) => e.target === currentNodeId);
        if (!incomingEdge) return false;

        const sourceNode = nodes.find((n) => n.id === incomingEdge.source);
        if (!sourceNode) return false;

        // guaranteed if invite accepted branch
        if (sourceNode?.data?.label === 'Send invite') {
          const cameFromAccepted =
            incomingEdge?.data?.branch === 'accepted' ||
            incomingEdge?.sourceHandle === 'accepted';
          if (cameFromAccepted) return true;
        }

        // guaranteed if IF connection YES branch
        if (sourceNode?.data?.label === 'Condition') {
          const ct = sourceNode?.data?.conditionType;
          const cameFromTrue =
            incomingEdge?.data?.branch === 'true' ||
            incomingEdge?.sourceHandle === 'true';
          if (ct === 'isConnection' && cameFromTrue) return true;
        }

        currentNodeId = incomingEdge.source;
      }
    },
    [edges, nodes]
  );

  const hasEmailGuaranteeInPath = useCallback(
    (nodeId) => {
      let currentNodeId = nodeId;

      while (true) {
        const incomingEdge = edges.find((e) => e.target === currentNodeId);
        if (!incomingEdge) return false;

        const sourceNode = nodes.find((n) => n.id === incomingEdge.source);
        if (!sourceNode) return false;

        // guaranteed if IF hasEmail YES branch
        if (sourceNode?.data?.label === 'Condition') {
          const ct = sourceNode?.data?.conditionType;
          const cameFromTrue =
            incomingEdge?.data?.branch === 'true' ||
            incomingEdge?.sourceHandle === 'true';

          if (ct === 'hasEmail' && cameFromTrue) return true;
        }

        currentNodeId = incomingEdge.source;
      }
    },
    [edges, nodes]
  );

  const canAddMessageFromSelectedNode = useCallback(
    (node) => {
      if (!node) return false;

      // End flow never allows children
      if (node?.data?.label === 'End flow') return false;

      // If selected is branch nodes, the branch choice matters.
      if (node?.data?.label === 'Send invite') {
        // Message allowed only on Accepted branch
        return pendingBranch === 'accepted';
      }

      if (node?.data?.label === 'Condition') {
        // Message allowed only on YES for isConnection
        return node?.data?.conditionType === 'isConnection' && pendingBranch === 'true';
      }

      // otherwise: already in a guaranteed path (came from accepted / connection yes)
      return hasConnectionGuaranteeInPath(node.id);
    },
    [pendingBranch, hasConnectionGuaranteeInPath]
  );

  const canAddEmailFromSelectedNode = useCallback(
    (node) => {
      if (!node) return false;
      if (node?.data?.label === 'End flow') return false;

      if (node?.data?.label === 'Condition') {
        return node?.data?.conditionType === 'hasEmail' && pendingBranch === 'true';
      }

      return hasEmailGuaranteeInPath(node.id);
    },
    [pendingBranch, hasEmailGuaranteeInPath]
  );

  const [isSendMessageDisabled, setIsSendMessageDisabled] = useState(true);
  useEffect(() => {
    if (!selectedNode) return setIsSendMessageDisabled(true);
    setIsSendMessageDisabled(!canAddMessageFromSelectedNode(selectedNode));
  }, [selectedNode, pendingBranch, canAddMessageFromSelectedNode]);

  const [isSendEmailDisabled, setIsSendEmailDisabled] = useState(true);
  useEffect(() => {
    if (!selectedNode) return setIsSendEmailDisabled(true);
    setIsSendEmailDisabled(!canAddEmailFromSelectedNode(selectedNode));
  }, [selectedNode, pendingBranch, canAddEmailFromSelectedNode]);

  // -------------------- onConnect rules --------------------
  const onConnect = useCallback(
    (params) => {
      const sourceNode = nodes.find((n) => n.id === params.source);
      const sourceLabel = sourceNode?.data?.label;

      // ✅ REQUIRED CHANGE: never allow outgoing from End flow
      if (sourceLabel === 'End flow') return;

      // one incoming per target (keeps your “single parent” model)
      const targetAlreadyHasIncoming = edges.some((e) => e.target === params.target);
      if (targetAlreadyHasIncoming) return;

      const isBranchSource = isBranchNodeLabel(sourceLabel);

      if (!isBranchSource) {
        // normal node: only 1 outgoing
        const sourceAlreadyHasOutgoing = edges.some((e) => e.source === params.source);
        if (sourceAlreadyHasOutgoing) return;
      } else {
        // branch node: only 1 outgoing per branch handle
        const branch = params.sourceHandle;
        if (!branch) return;
        const branchUsed = edges.some(
          (e) =>
            e.source === params.source &&
            ((e.data?.branch === branch) || (e.sourceHandle === branch))
        );
        if (branchUsed) return;
      }

      const branch = params.sourceHandle || undefined;

      setEdges((eds) =>
        addEdge(
          {
            ...params,
            data: branch ? { branch } : undefined,
          },
          eds
        )
      );
    },
    [edges, nodes, setEdges]
  );

  // -------------------- click handlers --------------------
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setNodeSettings(node.data || {});
    setShowSuccessMessage(false);
    setPendingBranch(null);
  }, []);

  // NodeHandlerContext will call this with (nodeId, branchId|null)
  const handleAddChildClick = useCallback(
    (nodeId, branch = null) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      // ✅ REQUIRED CHANGE: End flow should not allow adding actions
      if (node?.data?.label === 'End flow') return;

      if (isBranchNodeLabel(node?.data?.label)) {
        if (!branch) return;
        if (branchEdgeExists(node.id, branch)) return;
        setPendingBranch(branch);
      } else {
        setPendingBranch(null);
      }

      setSelectedNode(node);
      setShowAddActionModal(true);
    },
    [nodes, branchEdgeExists]
  );

  // -------------------- add node (returns positioned node) --------------------
  const addNodeBelowSelected = useCallback(
    (newNode) => {
      if (!selectedNode) return null;

      // ✅ REQUIRED CHANGE: prevent adding children under End flow
      if (selectedNode?.data?.label === 'End flow') return null;

      const parentX = selectedNode.position?.x ?? 0;
      const parentY = selectedNode.position?.y ?? 0;

      let x = parentX;
      let y = parentY + VERTICAL_GAP;

      let sourceHandle = 'out';
      let edgeData = undefined;

      const selectedLabel = selectedNode.data?.label;
      const selectedIsBranchNode = isBranchNodeLabel(selectedLabel);

      if (selectedIsBranchNode) {
        const branch = pendingBranch;
        if (!branch) return null;

        // place two branches left/right by default
        x =
          branch === BRANCH_NODE_META[selectedLabel]?.branches?.[0]?.id
            ? parentX + HORIZONTAL_GAP / 2
            : parentX - HORIZONTAL_GAP / 2;
        y = parentY + VERTICAL_GAP + 5;

        sourceHandle = branch;
        edgeData = { branch };
      } else {
        // start aesthetics
        if (selectedNode.id === 'start' || selectedLabel === 'Start') x -= 85;
      }

      // better overlap prevention
      const free = findFreePosition(x, y, { width: NODE_WIDTH, height: NODE_HEIGHT, stepY: 95 });

      const positionedNode = { ...newNode, position: free };

      setNodes((nds) => nds.concat(positionedNode));
      setEdges((eds) =>
        eds.concat({
          id: getEdgeId(),
          source: selectedNode.id,
          sourceHandle,
          target: positionedNode.id,
          targetHandle: 'in',
          data: edgeData,
        })
      );

      setPendingBranch(null);
      return positionedNode;
    },
    [selectedNode, pendingBranch, setNodes, setEdges, findFreePosition]
  );

  // -------------------- actions --------------------
  const connectionRequestExists = useCallback(() => nodes.some((n) => n.data.label === 'Send invite'), [nodes]);

  const onAddConnectionRequest = useCallback(() => {
    if (!selectedNode) return;

    if (selectedNode?.data?.label === 'End flow') return;

    if (nodes.some((n) => n.data.label === 'Send invite')) {
      alert('A "Connection request" node is already present in the flow.');
      return;
    }

    const newNode = {
      id: getNodeId(),
      data: {
        label: 'Send invite',
        ...LABEL_META['Send invite'],
        // compact timer settings for NOT accepted path (still used later by backend)
        notAcceptedWaitValue: 15,
        notAcceptedWaitUnit: 'minutes',
        // optional invite message
        sendMessage: false,
        message: '',
      },
      type: 'customNode',
      className: 'node-connection-request',
    };

    addNodeBelowSelected(newNode);
    setShowAddActionModal(false);
    onSave();
  }, [selectedNode, nodes, addNodeBelowSelected, onSave]);

  const onAddProfileView = useCallback(() => {
    if (selectedNode?.data?.label === 'End flow') return;

    const newNode = {
      id: getNodeId(),
      data: { label: 'Profile view', ...LABEL_META['Profile view'] },
      type: 'customNode',
      className: 'node-profile-view',
    };
    addNodeBelowSelected(newNode);
    setShowAddActionModal(false);
    onSave();
  }, [selectedNode, addNodeBelowSelected, onSave]);

  const onAddCondition = useCallback(() => {
    if (!selectedNode) return;
    if (selectedNode?.data?.label === 'End flow') return;

    const newNode = {
      id: getNodeId(),
      data: {
        label: 'Condition',
        ...LABEL_META['Condition'],
        conditionType: 'isConnection',
      },
      type: 'customNode',
      className: 'node-condition',
    };

    addNodeBelowSelected(newNode);
    setShowAddActionModal(false);
    onSave();
  }, [selectedNode, addNodeBelowSelected, onSave]);

  const onAddSendMessage = useCallback(() => {
    if (!selectedNode) return;

    if (!canAddMessageFromSelectedNode(selectedNode)) {
      alert('You can only add a "Send message" node after "Send invite: Accepted", or within "IF connection = Yes".');
      return;
    }

    const newNode = {
      id: getNodeId(),
      data: {
        label: 'Message',
        ...LABEL_META['Message'],
        message: '',
        // compact timer settings for NO response path
        noResponseWaitValue: 15,
        noResponseWaitUnit: 'minutes',
      },
      type: 'customNode',
      className: 'node-message',
    };

    addNodeBelowSelected(newNode);
    setShowAddActionModal(false);
    onSave();
  }, [selectedNode, canAddMessageFromSelectedNode, addNodeBelowSelected, onSave]);

  const onAddSendEmail = useCallback(() => {
    if (!selectedNode) return;

    if (!canAddEmailFromSelectedNode(selectedNode)) {
      alert('You can only add a "Send email" node after IF email exists = Yes, and only when the lead is a connection.');
      return;
    }

    const newNode = {
      id: getNodeId(),
      data: {
        label: 'Send email',
        ...LABEL_META['Send email'],
        emailSubject: '',
        emailBody: '',
        emailBodyHtml: '',
        attachmentAssetIds: [],
        // NEW default behavior: use global signature
        useGlobalSignature: true,
        emailSignatureOverride: '',
      },
      type: 'customNode',
      className: 'node-email',
    };

    addNodeBelowSelected(newNode);
    setShowAddActionModal(false);
    onSave();
  }, [selectedNode, canAddEmailFromSelectedNode, addNodeBelowSelected, onSave]);

  const onAddTimer = useCallback(() => {
    if (selectedNode?.data?.label === 'End flow') return;

    const newNode = {
      id: getNodeId(),
      data: { label: 'Timer', ...LABEL_META.Timer, duration: 15, timeUnit: 'minutes' },
      type: 'customNode',
      className: 'node-timer',
    };
    addNodeBelowSelected(newNode);
    setShowAddActionModal(false);
    onSave();
  }, [selectedNode, addNodeBelowSelected, onSave]);

  const onAddEndFlow = useCallback(() => {
    if (!selectedNode) return;

    // ✅ If already End flow, don't allow chaining end nodes
    if (selectedNode?.data?.label === 'End flow') return;

    const lastNode = nodes[nodes.length - 1];
    const parentCenterX = (lastNode.position?.x ?? 0) + (lastNode.width || NODE_WIDTH) / 2;
    const newXPosition = parentCenterX - END_NODE_WIDTH / 2;

    const initialPos = findFreePosition(newXPosition, (lastNode.position?.y ?? 0) + VERTICAL_GAP, { stepY: 95 });

    const endNode = {
      id: getNodeId(),
      data: { label: 'End flow', ...LABEL_META['End flow'] },
      type: 'customNode',
      position: initialPos,
      className: 'node-end',
    };

    // Add edge from selected -> end respecting branch if needed
    const selectedLabel = selectedNode.data?.label;
    const selectedIsBranchNode = isBranchNodeLabel(selectedLabel);

    if (selectedIsBranchNode) {
      if (!pendingBranch) {
        alert('Please choose a branch (+ button) to attach this step.');
        return;
      }
      setNodes((nds) => nds.concat(endNode));
      setEdges((eds) =>
        eds.concat({
          id: getEdgeId(),
          source: selectedNode.id,
          sourceHandle: pendingBranch,
          target: endNode.id,
          targetHandle: 'in',
          data: { branch: pendingBranch },
        })
      );
      setPendingBranch(null);
    } else {
      // normal
      setNodes((nds) => nds.concat(endNode));
      setEdges((eds) =>
        eds.concat({
          id: getEdgeId(),
          source: selectedNode.id,
          sourceHandle: 'out',
          target: endNode.id,
          targetHandle: 'in',
        })
      );
    }

    setShowAddActionModal(false);
    onSave();
  }, [selectedNode, pendingBranch, nodes, findFreePosition, setNodes, setEdges, onSave]);

  // -------------------- expose actions --------------------
  const handleLoadTemplateFromObject = useCallback((workflow) => {
    if (!workflow) return;
    const wf = hydrateWorkflow(workflow);
    const { nodes: savedNodes, edges: savedEdges, viewport } = wf;
    setNodes(savedNodes || []);
    setEdges(savedEdges || []);
    setTimeout(() => centerFlow?.(), 0);
    setViewport(viewport || { x: 0, y: 0, zoom: 1 });
  }, [setNodes, setEdges, setViewport, centerFlow]);

  const handleLoadTemplate = useCallback(async (templateId) => {
    if (templateId === '__EMPTY__') {
      setNodes([createStartNode()]);
      setEdges([]);
      setTimeout(() => centerFlow?.(), 0);
      setViewport({ x: 0, y: 0, zoom: 1 });
      return;
    }
    const { data } = await api.get(`/templates/workflow-templates/${templateId}`);
    handleLoadTemplateFromObject(data?.workflow);
  }, [handleLoadTemplateFromObject, setNodes, setEdges, setViewport, centerFlow]);

  useEffect(() => {
    if (exposeWorkflowActions && typeof exposeWorkflowActions === 'function') {
      exposeWorkflowActions({
        save: onSave,
        restore: onRestore,
        openSaveModal: () => setShowSaveModal(true),
        loadTemplate: handleLoadTemplate,
        loadTemplateFromObject: handleLoadTemplateFromObject,
      });
    }
  }, [onSave, onRestore, handleLoadTemplate, handleLoadTemplateFromObject, exposeWorkflowActions]);

  // -------------------- node settings --------------------
  const handleChange = (event) => {
    const { name, value } = event.target;
    setNodeSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setNodeSettings((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSaveSettings = () => {
    const selectedLabel = selectedNode?.data?.label;
    const linkedinMessage = String(nodeSettings.message || '');
    if (selectedLabel === 'Send invite' && nodeSettings.sendMessage && linkedinMessage.length > LINKEDIN_COMPOSER_LIMITS.invite) {
      setMessage({ type: 'error', text: `LinkedIn connection notes can contain at most ${LINKEDIN_COMPOSER_LIMITS.invite} characters.` });
      return;
    }
    if (selectedLabel === 'Message' && linkedinMessage.length > LINKEDIN_COMPOSER_LIMITS.message) {
      setMessage({ type: 'error', text: `LinkedIn messages can contain at most ${LINKEDIN_COMPOSER_LIMITS.message} characters.` });
      return;
    }

    const {
      // timer node
      duration,
      timeUnit,
      // invite/message text
      message,
      sendMessage,
      // condition
      conditionType,
      // compact timers
      notAcceptedWaitValue,
      notAcceptedWaitUnit,
      noResponseWaitValue,
      noResponseWaitUnit,
      // email
      emailSubject,
      emailBody,
      emailBodyHtml,
      attachmentAssetIds,
      // NEW
      useGlobalSignature,
      emailSignatureOverride,
    } = nodeSettings;

    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        if (node.id !== selectedNode?.id) return node;

        const lbl = node.data?.label;

        // timer node conversion to minutes storage (kept)
        let durationInMinutes = parseInt(duration) || 0;
        if (timeUnit === 'hours') durationInMinutes *= 60;
        else if (timeUnit === 'days') durationInMinutes *= 1440;

        return {
          ...node,
          data: {
            ...node.data,
            // Timer node
            duration: lbl === 'Timer' ? durationInMinutes : node.data?.duration,
            timeUnit: lbl === 'Timer' ? timeUnit : node.data?.timeUnit,

            // Invite/message content
            message: (lbl === 'Message' || lbl === 'Send invite') ? (message || '') : node.data?.message,
            sendMessage: lbl === 'Send invite' ? !!sendMessage : node.data?.sendMessage,

            // Email content
            emailSubject: lbl === 'Send email' ? (emailSubject || '') : node.data?.emailSubject,
            emailBody: lbl === 'Send email' ? (emailBody || '') : node.data?.emailBody,
            emailBodyHtml: lbl === 'Send email' ? (emailBodyHtml || '') : node.data?.emailBodyHtml,
            attachmentAssetIds: lbl === 'Send email' ? (Array.isArray(attachmentAssetIds) ? attachmentAssetIds.map(String) : []) : node.data?.attachmentAssetIds,

            // NEW signature behavior
            useGlobalSignature: lbl === 'Send email'
              ? (useGlobalSignature !== false) // default true
              : node.data?.useGlobalSignature,
            emailSignatureOverride: lbl === 'Send email'
              ? (emailSignatureOverride || '')
              : node.data?.emailSignatureOverride,

            // Condition
            conditionType: lbl === 'Condition' ? (conditionType || 'isConnection') : node.data?.conditionType,

            // Compact negative-branch timers
            notAcceptedWaitValue: lbl === 'Send invite' ? parseInt(notAcceptedWaitValue) || 15 : node.data?.notAcceptedWaitValue,
            notAcceptedWaitUnit: lbl === 'Send invite' ? (notAcceptedWaitUnit || 'minutes') : node.data?.notAcceptedWaitUnit,

            noResponseWaitValue: lbl === 'Message' ? parseInt(noResponseWaitValue) || 15 : node.data?.noResponseWaitValue,
            noResponseWaitUnit:  lbl === 'Message' ? (noResponseWaitUnit || 'minutes') : node.data?.noResponseWaitUnit,
          },
        };
      })
    );

    setShowSuccessMessage(true);
  };

  const insertAtCursor = (ref, value, token, key) => {
    const element = ref.current;
    const current = String(value || '');
    const start = Number.isInteger(element?.selectionStart) ? element.selectionStart : current.length;
    const end = Number.isInteger(element?.selectionEnd) ? element.selectionEnd : start;
    const next = current.slice(0, start) + token + current.slice(end);
    setNodeSettings((prev) => ({ ...prev, [key]: next }));
    window.requestAnimationFrame(() => { try { element?.focus(); element?.setSelectionRange(start + token.length, start + token.length); } catch (_) {} });
  };
  const handleInsertDynamicField = (field, target = 'body') => {
    const label = selectedNode?.data?.label;
    if (label === 'Send email' && target === 'subject') return insertAtCursor(subjectInputRef, nodeSettings.emailSubject, field, 'emailSubject');
    if (label === 'Send email') { richEmailEditorRef.current?.insertText(field); return; }
    return insertAtCursor(messageInputRef, nodeSettings.message, field, 'message');
  };

  const handleClosePanel = () => {
    setSelectedNode(null);
    setPendingBranch(null);
    setShowSuccessMessage(false);
  };

  const dynamicFieldGroups = [
    ['Lead', [['First name','{{firstName}}'],['Last name','{{lastName}}'],['Full name','{{fullName}}'],['Email','{{email}}'],['LinkedIn URL','{{linkedinUrl}}'],['Job title','{{jobTitle}}']]],
    ['Company', [['Company name','{{companyName}}'],['Website','{{companyWebsite}}'],['Industry','{{companyIndustry}}']]],
    ['Sender', [['Sender first name','{{senderFirstName}}'],['Sender full name','{{senderFullName}}'],['Sender email','{{senderEmail}}'],['Sender company','{{senderCompany}}']]],
    ['Campaign', [['Campaign name','{{campaignName}}']]],
  ];

  const renderDynamicFieldsDropdown = (target = 'body') => (
    <Dropdown>
      <Dropdown.Toggle id={`dropdown-dynamic-field-${target}`} className="!bg-white !text-gray-700 !border !border-gray-300 !text-xs !px-2 !py-1 !rounded-md hover:!bg-gray-50">Add dynamic field</Dropdown.Toggle>
      <Dropdown.Menu className="max-h-80 overflow-y-auto">
        {dynamicFieldGroups.flatMap(([group, fields]) => [
          <Dropdown.Header key={`${group}-header`}>{group}</Dropdown.Header>,
          ...fields.map(([label, token]) => <Dropdown.Item key={token} onClick={() => handleInsertDynamicField(token, target)}>{label} <span className="text-gray-400 text-[10px]">{token}</span></Dropdown.Item>)
        ])}
      </Dropdown.Menu>
    </Dropdown>
  );

  const SavedNote = () =>
    showSuccessMessage ? <div className="mt-2 text-xs text-green-600">Saved successfully!</div> : null;

  const renderCompactWait = (prefix, title) => {
    // prefix: "notAcceptedWait" or "noResponseWait"
    const valueName = `${prefix}Value`;
    const unitName = `${prefix}Unit`;

    return (
      <div className="mt-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
        <div className="text-xs font-semibold text-gray-700 mb-2">{title}</div>
        <div className="grid grid-cols-2 gap-2">
          <Form.Group>
            <Form.Label className="text-xs text-gray-600">Value</Form.Label>
            <Form.Control
              type="number"
              min={1}
              step={1}
              name={valueName}
              value={nodeSettings[valueName] ?? 15}
              onChange={handleChange}
              placeholder="Enter value"
              className="mt-1 block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="text-xs text-gray-600">Unit</Form.Label>
            <Form.Control
              as="select"
              name={unitName}
              value={nodeSettings[unitName] ?? 'minutes'}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </Form.Control>
          </Form.Group>
        </div>

        <div className="mt-2 text-[11px] text-gray-500">
          This replaces the separate Timer node. The backend should treat this as an <b>implicit timer</b> before continuing on that branch.
        </div>
      </div>
    );
  };

  // --------------------
  // NEW: compute signature + preview for Send email node (prevents double footer)
  // --------------------
  const effectiveSignature = useMemo(() => {
    const useGlobal = nodeSettings.useGlobalSignature !== false;
    if (!useGlobal) return normalizeSig(nodeSettings.emailSignatureOverride);
    return normalizeSig(globalSignature);
  }, [nodeSettings.useGlobalSignature, nodeSettings.emailSignatureOverride, globalSignature]);

  const signatureEnabled = useMemo(() => {
    return !!normalizeSig(effectiveSignature);
  }, [effectiveSignature]);

  const bodyHasSignatureAlready = useMemo(() => {
    const body = String(nodeSettings.emailBody || '');
    const sig = normalizeSig(effectiveSignature);
    if (!sig) return false;
    return body.includes(sig);
  }, [nodeSettings.emailBody, effectiveSignature]);

  const previewEmailBody = useMemo(() => {
    const body = String(nodeSettings.emailBody || '');
    if (!signatureEnabled) return body;

    // If user already pasted it, don't append in preview
    if (bodyHasSignatureAlready) return body;

    const sig = sigBlock(effectiveSignature);
    if (!sig) return body;

    if (!body.trim()) return `${sig}`;
    return `${body}\n\n${sig}`;
  }, [nodeSettings.emailBody, signatureEnabled, bodyHasSignatureAlready, effectiveSignature]);

  const renderNodeSettings = () => {
    if (!selectedNode) return null;

    const label = selectedNode.data?.label;
    const isStartNode = selectedNode.id === 'start' || label === 'Start';
    const isEndNode = label === 'End flow';

    const hasIncomingEdges = edges.some((edge) => edge.target === selectedNode.id);
    const hasOutgoingEdges = edges.some((edge) => edge.source === selectedNode.id);

    const canDeleteNode =
      campaignStatus === 'Concept' &&
      !isStartNode &&
      (!hasIncomingEdges || !hasOutgoingEdges);

    const actions = (
      <div className="mt-4 flex items-center gap-2 justify-end">
        <button
          className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md border transition focus:outline-none bg-blue-600 text-white border-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-300"
          onClick={handleSaveSettings}
          type="button"
        >
          Save
        </button>

        {canDeleteNode && (
          <button
            className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md border transition focus:outline-none bg-white text-red-600 border-red-300 hover:bg-red-50"
            type="button"
            onClick={() => {
              const deleteId = selectedNode.id;
              setNodes((nds) => nds.filter((n) => n.id !== deleteId));
              setEdges((eds) => eds.filter((e) => e.source !== deleteId && e.target !== deleteId));
              setSelectedNode(null);
              setPendingBranch(null);
              onSave();
            }}
          >
            Delete Node
          </button>
        )}
      </div>
    );

    const ConditionIncompleteNote = () => {
      if (label !== 'Condition') return null;
      const hasYes = branchEdgeExists(selectedNode.id, 'true');
      const hasNo = branchEdgeExists(selectedNode.id, 'false');
      if (hasYes && hasNo) return null;
      return (
        <div className="mt-2 text-xs text-amber-600">
          This condition is incomplete. Connect both branches (Yes / No) using the + buttons inside the node.
        </div>
      );
    };

    const BranchNodeIncompleteNote = () => {
      if (label !== 'Send invite' && label !== 'Message') return null;
      const branches = BRANCH_NODE_META[label].branches;
      const allConnected = branches.every((b) => branchEdgeExists(selectedNode.id, b.id));
      if (allConnected) return null;
      return (
        <div className="mt-2 text-xs text-amber-600">
          This step is incomplete. Connect both outcomes using the + buttons inside the node.
        </div>
      );
    };

    if (label === 'Timer') {
      return (
        <Form>
          <Form.Group controlId="timerDuration">
            <Form.Label className="text-sm text-gray-700">Set wait time</Form.Label>
            <Form.Control
              type="number"
              name="duration"
              value={nodeSettings.duration || ''}
              onChange={handleChange}
              placeholder="Enter value"
              className="mt-1 block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
          </Form.Group>

          <Form.Group controlId="timerTimeUnit" className="mt-3">
            <Form.Label className="text-sm text-gray-700">Time Unit</Form.Label>
            <Form.Control
              as="select"
              name="timeUnit"
              value={nodeSettings.timeUnit}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </Form.Control>
          </Form.Group>

          {actions}
          <SavedNote />
        </Form>
      );
    }

    if (label === 'Condition') {
      return (
        <Form>
          <Form.Group controlId="conditionType">
            <Form.Label className="text-sm text-gray-700">Condition</Form.Label>
            <Form.Control
              as="select"
              name="conditionType"
              value={nodeSettings.conditionType || 'isConnection'}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="isConnection">If connection</option>
              <option value="hasLinkedinUrl">If linkedin url exists</option>
              <option value="hasEmail">If email exists</option>
            </Form.Control>

            <div className="mt-2 text-xs text-gray-500">
              This splits your flow into <b>Yes</b> and <b>No</b> branches for each lead.
            </div>

            <ConditionIncompleteNote />
          </Form.Group>

          {actions}
          <SavedNote />
        </Form>
      );
    }

    if (label === 'Send invite') {
      return (
        <Form>
          <Form.Group controlId="sendConnectionRequestCheckbox">
            <Form.Check
              type="checkbox"
              label="Send message with request"
              name="sendMessage"
              checked={nodeSettings.sendMessage || false}
              onChange={handleCheckboxChange}
              className="text-sm"
            />
          </Form.Group>

          {nodeSettings.sendMessage && (
            <Form.Group controlId="inviteMessage" className="mt-3">
              <Form.Label className="text-sm text-gray-700">Message to send</Form.Label>
              {renderDynamicFieldsDropdown()}
              <Form.Control
                as="textarea"
                ref={messageInputRef}
                name="message"
                value={nodeSettings.message || ''}
                onChange={handleChange}
                maxLength={LINKEDIN_COMPOSER_LIMITS.invite}
                rows={5}
                className="mt-2 block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
              />
              <div className="mt-2 flex items-start justify-between gap-3 text-[11px]">
                <span className="text-gray-500">Plain text · dynamic fields are resolved per lead · no attachments</span>
                <span className={String(nodeSettings.message || '').length > LINKEDIN_COMPOSER_LIMITS.invite * 0.9 ? 'font-semibold text-amber-600' : 'text-gray-400'}>
                  {String(nodeSettings.message || '').length}/{LINKEDIN_COMPOSER_LIMITS.invite}
                </span>
              </div>
            </Form.Group>
          )}

          {renderCompactWait('notAcceptedWait', 'If NOT accepted → wait before continuing')}

          <BranchNodeIncompleteNote />

          {actions}
          <SavedNote />
        </Form>
      );
    }

    if (label === 'Message') {
      return (
        <Form>
          <Form.Group controlId="message">
            <Form.Label className="text-sm text-gray-700">Message to send</Form.Label>
            {renderDynamicFieldsDropdown()}
            <Form.Control
              as="textarea"
              ref={messageInputRef}
              name="message"
              value={nodeSettings.message || ''}
              onChange={handleChange}
              maxLength={LINKEDIN_COMPOSER_LIMITS.message}
              rows={7}
              className="mt-2 block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
            <div className="mt-2 flex items-start justify-between gap-3 text-[11px]">
              <span className="text-gray-500">Plain text · only runs for confirmed connections · dynamic fields are resolved per lead · no attachments</span>
              <span className={String(nodeSettings.message || '').length > LINKEDIN_COMPOSER_LIMITS.message * 0.9 ? 'font-semibold text-amber-600' : 'text-gray-400'}>
                {String(nodeSettings.message || '').length}/{LINKEDIN_COMPOSER_LIMITS.message}
              </span>
            </div>
          </Form.Group>

          {renderCompactWait('noResponseWait', 'If NO response → wait before continuing')}

          <BranchNodeIncompleteNote />

          {actions}
          <SavedNote />
        </Form>
      );
    }

    // ✅ UPDATED: Send email settings now show signature behavior + preview, and prevents double signature
    if (label === 'Send email') {
      return (
        <Form>
          <Form.Group controlId="emailSubject">
            <Form.Label className="text-sm text-gray-700">Email subject</Form.Label>
            {renderDynamicFieldsDropdown('subject')}
            <Form.Control
              type="text"
              ref={subjectInputRef}
              name="emailSubject"
              value={nodeSettings.emailSubject || ''}
              onChange={handleChange}
              className="mt-2 block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
              placeholder="Subject..."
            />
          </Form.Group>

          {/* Signature toggle */}
          <div className="mt-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-semibold text-gray-700">Email footer / signature</div>
                <div className="text-[11px] text-gray-500 mt-1">
                  By default we append your global signature from Settings. You can override it per email step.
                </div>
              </div>

              <div className="text-[11px] text-gray-500 pt-0.5">
                {signatureLoading ? 'Loading…' : signatureEnabled ? 'Enabled' : 'Empty'}
              </div>
            </div>

            <div className="mt-3">
              <Form.Check
                type="checkbox"
                id="useGlobalSignature"
                name="useGlobalSignature"
                label="Use global signature (recommended)"
                checked={nodeSettings.useGlobalSignature !== false}
                onChange={(e) =>
                  setNodeSettings((prev) => ({ ...prev, useGlobalSignature: e.target.checked }))
                }
                className="text-sm"
              />
            </div>

            {nodeSettings.useGlobalSignature === false && (
              <div className="mt-3">
                <div className="text-xs text-gray-600 mb-1">Override signature for this email</div>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="emailSignatureOverride"
                  value={nodeSettings.emailSignatureOverride || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
                  placeholder="Best regards,\nJohn Doe\nCompany"
                />
              </div>
            )}

            {/* Duplicate warning */}
            {signatureEnabled && bodyHasSignatureAlready && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <div className="text-xs font-semibold text-amber-900">Signature detected in your email body</div>
                <div className="text-xs text-amber-800 mt-1">
                  To prevent sending it twice, remove the footer from the body or disable the signature toggle above.
                </div>

                <button
                  type="button"
                  className="mt-2 text-xs px-3 py-1.5 rounded-md border bg-white hover:bg-amber-100 text-amber-900 border-amber-200"
                  onClick={() => {
                    const sig = normalizeSig(effectiveSignature);
                    setNodeSettings((prev) => ({
                      ...prev,
                      emailBody: String(prev.emailBody || '').replace(sig, '').trim(),
                    }));
                  }}
                >
                  Remove duplicate from body
                </button>
              </div>
            )}
          </div>

          <Form.Group controlId="emailBody" className="mt-3">
            <Form.Label className="text-sm text-gray-700">Email body</Form.Label>
            {renderDynamicFieldsDropdown()}
            <div className="mt-2">
              <RichEmailEditor ref={richEmailEditorRef} value={nodeSettings.emailBodyHtml || nodeSettings.emailBody || ''} onChange={(html) => setNodeSettings((prev) => ({ ...prev, emailBodyHtml: html, emailBody: html.replace(/<br\s*\/?\s*>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim() }))} />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Emails can only run when the lead is a connection and when <b>IF email exists = Yes</b> is guaranteed in the path.
            </div>
          </Form.Group>

          <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-start justify-between gap-3"><div><div className="text-xs font-semibold text-gray-700">Attachments</div><div className="mt-1 text-[11px] text-gray-500">Choose from files uploaded to this campaign. Manage the library with the Assets button above the workflow.</div></div><button type="button" onClick={refreshCampaignAssets} className="text-[11px] font-medium text-blue-600 hover:underline">Refresh</button></div>
            <div className="mt-2 space-y-1.5">{campaignAssets.filter((asset) => (asset.channels || []).includes('email')).map((asset) => { const selected=(nodeSettings.attachmentAssetIds||[]).map(String).includes(String(asset._id)); return <label key={asset._id} className={`flex cursor-pointer items-start gap-2 rounded-md border px-2.5 py-2 text-xs ${selected?'border-blue-200 bg-blue-50':'border-gray-200 bg-white'}`}><input type="checkbox" className="mt-0.5" checked={selected} onChange={(e) => setNodeSettings((prev) => { const ids=(prev.attachmentAssetIds||[]).map(String); return {...prev,attachmentAssetIds:e.target.checked?[...new Set([...ids,String(asset._id)])]:ids.filter((x)=>x!==String(asset._id))}; })}/><span className="min-w-0"><span className="block truncate font-medium text-gray-800">{asset.name}</span>{asset.description&&<span className="mt-0.5 block text-gray-500">{asset.description}</span>}</span></label>; })}{!campaignAssets.length&&<div className="rounded-md bg-white px-3 py-3 text-xs text-gray-500">No campaign assets yet.</div>}</div>
          </div>

          {/* Preview */}
          <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs font-semibold text-gray-700">Preview</div>
              <div className="text-[11px] text-gray-500">
                {signatureLoading
                  ? 'Loading signature…'
                  : signatureEnabled
                    ? bodyHasSignatureAlready
                      ? 'Signature already included in body'
                      : 'Signature will be appended'
                    : 'No signature'}
              </div>
            </div>

            {nodeSettings.emailBodyHtml ? <iframe title="Email preview" sandbox="" srcDoc={`<!doctype html><html><body style="font-family:Arial,sans-serif;font-size:14px;line-height:1.55;padding:12px;color:#1f2937">${nodeSettings.emailBodyHtml}${signatureEnabled && !bodyHasSignatureAlready ? `<br/><br/>${String(effectiveSignature || '').replace(/\n/g,'<br/>')}` : ''}</body></html>`} className="mt-2 h-52 w-full rounded-md border border-gray-200 bg-white" /> : <pre className="mt-2 whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-md p-3">{previewEmailBody || '—'}</pre>}
          </div>

          {actions}
          <SavedNote />
        </Form>
      );
    }

    if (isEndNode) {
      return (
        <div>
          <div className="text-sm text-gray-700">
            This is an <b>End flow</b> node. No steps can be added after this node.
          </div>

          {actions}
          <SavedNote />
        </div>
      );
    }

    return (
      <div>
        <p className="text-sm text-gray-600">No settings available for this node.</p>
        {!hasOutgoingEdges && !isStartNode && campaignStatus === 'Concept' && (
          <div className="mt-4 flex items-center justify-end">
            <button
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md border transition focus:outline-none bg-white text-red-600 border-red-300 hover:bg-red-50"
              type="button"
              onClick={() => {
                const deleteId = selectedNode.id;
                setNodes((nds) => nds.filter((n) => n.id !== deleteId));
                setEdges((eds) => eds.filter((e) => e.source !== deleteId && e.target !== deleteId));
                setSelectedNode(null);
                setPendingBranch(null);
                onSave();
              }}
            >
              Delete Node
            </button>
          </div>
        )}
      </div>
    );
  };

  // -------------------- Template Modals (unchanged) --------------------
  const handleSaveAsTemplate = async () => {
    if (!templateName || !rfInstance) return;
    const flow = rfInstance.toObject();
    try {
      const org =
        currentUser?.organizationId?.$oid ||
        (typeof currentUser?.organizationId === 'string' ? currentUser.organizationId : null);

      await api.post('/templates/workflow-templates', {
        name: templateName,
        workflow: flow,
        createdBy: currentUser?.email || Userfront.user?.email,
        creatorName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim(),
        organizationId: org || null,
      });

      setMessage({ type: 'success', text: 'Template saved successfully!' });
      refreshTemplates();
      setTemplateName('');
      setShowSaveModal(false);
    } catch (err) {
      console.error('Failed to save template:', err);
      setMessage({ type: 'error', text: 'Failed to save template.' });
    }
  };

  const modalTitle = useMemo(() => {
    if (!selectedNode) return 'Add action';
    const lbl = selectedNode.data?.label;
    if (!isBranchNodeLabel(lbl) || !pendingBranch) return 'Add action';
    const branchLabel = (BRANCH_NODE_META[lbl]?.branches || []).find((b) => b.id === pendingBranch)?.label;
    return branchLabel ? `Add action (${branchLabel})` : 'Add action';
  }, [selectedNode, pendingBranch]);

  return (
    <div
      className="reactflow-wrapper"
      style={{ width: '100%', height: '70vh', position: 'relative' }}
      ref={reactFlowWrapper}
    >
      <NodeHandlerContext.Provider value={handleAddChildClick}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onInit={(instance) => {
            setRfInstance(instance);
            centerFlow();
          }}
          zoomOnScroll={true}
          panOnScroll={false}
          panOnDrag={true}
          zoomOnDoubleClick={false}
          minZoom={0.6}
          maxZoom={1}
          deleteKeyCode={''}
        >
          <Background color="#000" variant={BackgroundVariant.Dots} />

          {selectedNode && (
            <Panel position="top-right" className="panel-workflow nodrag nowheel">
              <div className="w-[380px] h-[calc(70vh-16px)] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden">
                <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b bg-white">
                  <h3 className="text-sm font-semibold text-gray-900">Node settings</h3>
                  <button
                    onClick={handleClosePanel}
                    className="rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-100 text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    aria-label="Close panel"
                    type="button"
                  >
                    <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
                  </button>
                </div>

                <div
                  className="p-4 overflow-y-auto"
                  onWheel={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                >
                  {renderNodeSettings() || (
                    <div className="p-4 bg-gray-50 text-sm text-gray-500 rounded-md">
                      No settings available for this node.
                    </div>
                  )}
                </div>
              </div>
            </Panel>
          )}
        </ReactFlow>
      </NodeHandlerContext.Provider>

      {showSaveModal && (
        <div className="fixed inset-0 bg-white/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-100 w-full max-w-md p-6 rounded-xl shadow-xl border">
            <h3 className="text-xl font-semibold mb-4">{t('campaigns.saveTemplate')}</h3>
            <input
              type="text"
              placeholder={t('campaigns.templateNamePlaceholder')}
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveModal(false)}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm"
                type="button"
              >
                {t('campaigns.cancel')}
              </button>
              <button
                onClick={handleSaveAsTemplate}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm"
                type="button"
              >
                {t('campaigns.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoadModal && (
        <div className="fixed inset-0 bg-white/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-100 w-full max-w-md p-6 rounded-xl shadow-xl border">
            <h3 className="text-xl font-semibold mb-4">{t('campaigns.selectTemplate')}</h3>
            <ul className="max-h-60 overflow-y-auto mb-4">
              {templateList.map((template) => (
                <li
                  key={template._id}
                  onClick={() => {
                    // keep your existing behavior
                    const wf = hydrateWorkflow(template?.workflow);
                    setNodes(wf?.nodes || []);
                    setEdges(wf?.edges || []);
                    setTimeout(centerFlow, 0);
                    setShowLoadModal(false);
                  }}
                  className="px-3 py-2 rounded-md hover:bg-white border cursor-pointer text-sm text-gray-700"
                >
                  {template.name}
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <button
                onClick={() => setShowLoadModal(false)}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm"
                type="button"
              >
                {t('campaigns.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddActionModal && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-sm p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-1">{modalTitle}</h3>

            {isBranchNodeLabel(selectedNode?.data?.label) && pendingBranch && (
              <div className="text-xs text-gray-500 mb-4">
                You’re adding a step for: <b>{modalTitle.replace('Add action (', '').replace(')', '')}</b>
              </div>
            )}

            <div className="space-y-2">
              <button
                onClick={onAddConnectionRequest}
                disabled={connectionRequestExists()}
                className="w-full flex items-center justify-between px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                <span>Send invite</span>
              </button>

              <button
                onClick={onAddProfileView}
                className="w-full flex items-center justify-between px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                type="button"
              >
                <span>Profile view</span>
              </button>

              <button
                onClick={onAddCondition}
                className="w-full flex items-center justify-between px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                type="button"
              >
                <span>IF condition</span>
              </button>

              <button
                onClick={onAddSendMessage}
                disabled={isSendMessageDisabled}
                className="w-full flex items-center justify-between px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                <span>Send message</span>
              </button>

              <button
                onClick={onAddSendEmail}
                disabled={isSendEmailDisabled}
                className="w-full flex items-center justify-between px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                <span>Send email</span>
              </button>

              <button
                onClick={onAddTimer}
                className="w-full flex items-center justify-between px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                type="button"
              >
                <span>Add timer (explicit)</span>
              </button>

              <button
                onClick={onAddEndFlow}
                className="w-full flex items-center justify-between px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                type="button"
              >
                <span>End flow</span>
              </button>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setShowAddActionModal(false);
                  setPendingBranch(null);
                }}
                className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateBuilder;